import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { dashboardApi } from '@/services/api'
import Card from '@/components/ui/Card'
import Loading from '@/components/ui/Loading'
import PageNotification from '@/components/ui/PageNotification'
import { formatCurrency, formatRelativeTime } from '@/utils/format'
import { Package, GitMerge, Clock, Coins } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'

export default function Dashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.get(),
  })

  // Handle return from Stripe subscription checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id')
    if (sessionId) {
      // Refresh subscription and user data
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })

      // Clean up URL
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('session_id')
      setSearchParams(newSearchParams, { replace: true })
    }
  }, [searchParams, queryClient, setSearchParams])

  if (isLoading) return <Loading />

  const stats = data?.data || {
    my_barters_count: 0,
    active_groups_count: 0,
    pending_requests_count: 0,
    barter_coin_balance: 0,
    recent_transactions: []
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of your bartering activity</p>
      </div>

      <PageNotification pageRoute="dashboard" />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/barters')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">My Barters</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.my_barters_count || 0}</p>
            </div>
            <Package className="w-12 h-12 text-primary-600" />
          </div>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/groups')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Groups</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.active_groups_count || 0}</p>
            </div>
            <GitMerge className="w-12 h-12 text-green-600" />
          </div>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Requests</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.pending_requests_count || 0}</p>
            </div>
            <Clock className="w-12 h-12 text-yellow-600" />
          </div>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/coins')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Barter Coins</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.barter_coin_balance || 0}</p>
            </div>
            <Coins className="w-12 h-12 text-purple-600" />
          </div>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card title="Recent Transactions">
        {stats.recent_transactions && stats.recent_transactions.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_transactions.map((transaction: any) => (
              <div key={transaction.id} className="flex justify-between items-center py-3 border-b last:border-b-0">
                <div>
                  <p className="font-medium text-gray-900">{transaction.description}</p>
                  <p className="text-sm text-gray-500">{formatRelativeTime(transaction.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${transaction.transaction_type.includes('in') || transaction.transaction_type === 'purchase' ? 'text-green-600' : 'text-red-600'}`}>
                    {transaction.transaction_type.includes('in') || transaction.transaction_type === 'purchase' ? '+' : '-'}
                    {formatCurrency(transaction.coin_amount)} coins
                  </p>
                  <p className="text-sm text-gray-500 capitalize">{transaction.status}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No recent transactions</p>
        )}
      </Card>

      {/* Quick Actions */}
      <Card title="Quick Actions">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button onClick={() => navigate('/dashboard/barters/create')} className="btn btn-primary py-4">
            Create Barter
          </button>
          <button onClick={() => navigate('/dashboard/coins')} className="btn btn-secondary py-4">
            Buy Coins
          </button>
          <button onClick={() => navigate('/dashboard/support')} className="btn btn-secondary py-4">
            Get Support
          </button>
          <button onClick={() => navigate('/dashboard/profile')} className="btn btn-secondary py-4">
            Edit Profile
          </button>
        </div>
      </Card>
    </div>
  )
}
